import { z } from "zod";

export const SESSION_KEY = "centro.session";
// The Auth0 SPA SDK caches its tokens in localStorage under keys with this prefix.
const AUTH0_SDK_CACHE_PREFIX = "@@auth0spajs@@";

const storedSessionSchema = z.object({
  provider: z.enum(["password", "sso"]),
  keepSignedIn: z.boolean(),
  accessToken: z.string().min(1),
  idToken: z.string().min(1).optional(),
  // Password logins only; SSO refresh tokens live in the Auth0 SDK's cache.
  refreshToken: z.string().min(1).optional(),
});

export type StoredSession = z.infer<typeof storedSessionSchema>;
export type SessionProvider = StoredSession["provider"];

export interface SessionTokens {
  readonly accessToken: string;
  readonly idToken?: string;
  readonly refreshToken?: string;
}

export interface SaveSessionOptions {
  readonly keepSignedIn: boolean;
  readonly provider: SessionProvider;
}

/** The session to store for a login. Without keepSignedIn, the refresh token is dropped. */
export const buildStoredSession = (
  tokens: SessionTokens,
  { keepSignedIn, provider }: SaveSessionOptions,
): StoredSession => ({
  provider,
  keepSignedIn,
  accessToken: tokens.accessToken,
  ...(tokens.idToken && { idToken: tokens.idToken }),
  ...(keepSignedIn &&
    tokens.refreshToken && { refreshToken: tokens.refreshToken }),
});

const parseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

/** The stored session, or undefined for a missing, unreadable, or wrongly shaped value. */
export const parseSession = (raw: string | null): StoredSession | undefined => {
  if (raw === null) return undefined;
  const parsed = storedSessionSchema.safeParse(parseJson(raw));
  return parsed.success ? parsed.data : undefined;
};

/** Of the given storage keys, the ones holding auth data, which a logout removes. */
export const authKeysIn = (keys: readonly string[]): string[] =>
  keys.filter(
    (key) => key === SESSION_KEY || key.startsWith(AUTH0_SDK_CACHE_PREFIX),
  );

/** Whether a storage event's key affects the session; a null key means storage was cleared. */
export const isSessionChange = (key: string | null): boolean =>
  key === SESSION_KEY || key === null;

type SessionListener = () => void;

const listeners = new Set<SessionListener>();

const notifyListeners = (): void => {
  listeners.forEach((listener) => listener());
};

/** The stored session's raw value; stable between changes, for useSyncExternalStore. */
export const getSessionSnapshot = (): string | null =>
  typeof window === "undefined"
    ? null
    : window.localStorage.getItem(SESSION_KEY);

export const readSession = (): StoredSession | undefined =>
  parseSession(getSessionSnapshot());

export const storeSession = (session: StoredSession): void => {
  window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  notifyListeners();
};

/** Stores the tokens from a successful login. */
export const saveSession = (
  tokens: SessionTokens,
  options: SaveSessionOptions,
): void => storeSession(buildStoredSession(tokens, options));

/** Removes the app's session but keeps the Auth0 SDK's cache, which an SSO revoke still reads. */
export const removeSession = (): void => {
  window.localStorage.removeItem(SESSION_KEY);
  notifyListeners();
};

export const clearSession = (): void => {
  authKeysIn(Object.keys(window.localStorage)).forEach((key) =>
    window.localStorage.removeItem(key),
  );
  notifyListeners();
};

/**
 * Calls the listener whenever the session changes, in this tab or another one. The
 * browser's storage event only fires in the other tabs, which is what makes a logout
 * in one tab sign out the rest.
 */
export const subscribeToSession = (listener: SessionListener): (() => void) => {
  const onStorage = (event: StorageEvent): void => {
    if (isSessionChange(event.key)) listener();
  };
  listeners.add(listener);
  window.addEventListener("storage", onStorage);

  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
};
